import { Link, Navigate, Outlet, useLocation, useParams, useRouteError } from 'react-router-dom'
import type { ReactNode } from 'react'

import { LogoMark } from '@/components/layout/Logo'
import { Button, Spinner } from '@/components/layout/ui'
import { NetworkError } from '@/lib/api'
import { useSession } from '@/lib/session'
import { buildWorkspace, WorkspaceProvider } from '@/lib/workspace'
import NotFound from './NotFound'

/** Full-page loader for the moment before the session is known. */
export function FullPageLoader({ label = 'Loading Taskly…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3">
      <LogoMark className="size-9" />
      <p className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
        <Spinner className="size-4" />
        {label}
      </p>
    </div>
  )
}

/**
 * Shown when the session could not be read at all.
 *
 * This is deliberately not a login form: a network failure or a 500 does not
 * mean the account is gone, and asking someone to re-enter a password because a
 * proxy hiccuped teaches them the app loses sessions.
 */
function SessionUnavailable({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const offline = error instanceof NetworkError
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
      <LogoMark className="size-9" />
      <h1 className="mt-4 text-lg font-semibold tracking-tight">
        {offline ? 'Taskly is unreachable' : 'Something went wrong'}
      </h1>
      <p className="mt-1.5 max-w-sm text-sm text-ink-500 dark:text-ink-400">
        {offline
          ? 'Check your connection and try again. Your session is still valid.'
          : 'The server could not confirm your session. This is usually temporary.'}
      </p>
      <Button variant="primary" className="mt-6" onClick={onRetry}>
        Try again
      </Button>
    </main>
  )
}

/** Gate for every authenticated route. */
export function RequireAuth() {
  const session = useSession()
  const location = useLocation()

  if (session.status === 'loading') return <FullPageLoader />
  if (session.status === 'error') {
    return <SessionUnavailable error={session.error} onRetry={() => void session.refresh()} />
  }
  if (session.status === 'anonymous') {
    // Remember where they were headed so signing in resumes it.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  return <Outlet />
}

/**
 * Keeps a signed-in user off /login and /register.
 *
 * It honours the same `state.from` the sign-in redirect sets, because this — not
 * the form's own success handler — is what actually runs the moment the session
 * lands. Without it, someone who followed an invitation link and signed up would
 * be bounced to their own workspace and lose the invitation they came for.
 */
export function RequireAnonymous({ children }: { children: ReactNode }) {
  const session = useSession()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from

  if (session.status === 'loading') return <FullPageLoader />
  if (session.status === 'authenticated') return <Navigate to={from ?? '/'} replace />
  return <>{children}</>
}

/** `/` — send the user to whichever workspace the server says is active. */
export function WorkspaceRedirect() {
  const { defaultOrganization } = useSession()
  if (!defaultOrganization) return <Navigate to="/new-workspace" replace />
  return <Navigate to={`/${defaultOrganization.slug}`} replace />
}

/**
 * Resolves `:slug` against the caller's own memberships.
 *
 * The membership list comes from the session, which is derived server-side from
 * organization_members — so an unknown slug and a slug belonging to someone
 * else's workspace are indistinguishable here, exactly as they are in the API.
 */
export function WorkspaceGate() {
  const { slug } = useParams<{ slug: string }>()
  const { organizations, defaultOrganization } = useSession()
  const membership = organizations.find((org) => org.slug === slug)

  if (!membership) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
        <p className="text-sm font-medium text-brand-600 dark:text-brand-400">404</p>
        <h1 className="mt-2 text-xl font-semibold tracking-tight">Workspace not found</h1>
        <p className="mt-1.5 max-w-sm text-sm text-ink-500 dark:text-ink-400">
          It may have been deleted, or your access to it removed.
        </p>
        {defaultOrganization ? (
          <Link
            to={`/${defaultOrganization.slug}`}
            className="mt-6 inline-flex h-10 items-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            Go to {defaultOrganization.name}
          </Link>
        ) : null}
      </main>
    )
  }

  return (
    <WorkspaceProvider value={buildWorkspace(membership)}>
      <Outlet />
    </WorkspaceProvider>
  )
}

/**
 * Gate for the platform admin console.
 *
 * It renders the ordinary 404 page rather than redirecting or explaining. That
 * is the same answer the API gives: every `/api/v2/admin/*` route replies 404,
 * not 403, to a caller without `users.is_admin`, so that probing for the
 * console is indistinguishable from mistyping a URL. A client that said
 * "you are not an administrator" would give away exactly what the server
 * refuses to.
 *
 * This is a rendering decision and never an authorisation one — the flag comes
 * from the session payload, and forging it locally buys a shell whose every
 * request comes back 404.
 */
export function RequirePlatformAdmin() {
  const { status, isPlatformAdmin } = useSession()

  if (status === 'loading') return <FullPageLoader />
  if (!isPlatformAdmin) return <NotFound />
  return <Outlet />
}

/** Pathless root, so one error boundary covers every route below it. */
export function RootLayout() {
  return <Outlet />
}

/**
 * Last line of defence for a render-time crash.
 *
 * Without an `errorElement`, React Router shows its own developer overlay —
 * a stack trace, in production, to whoever happens to be looking. This shows
 * something a person can act on and keeps the detail in the console.
 */
export function RouteErrorPage() {
  const error = useRouteError()
  if (error) console.error(error)

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
      <LogoMark className="size-9" />
      <h1 className="mt-4 text-xl font-semibold tracking-tight">This page hit a snag</h1>
      <p className="mt-1.5 max-w-sm text-sm text-ink-500 dark:text-ink-400">
        Reloading usually clears it. If it keeps happening, the details are in the browser console.
      </p>
      <div className="mt-6 flex gap-2">
        <Button variant="primary" onClick={() => window.location.reload()}>
          Reload
        </Button>
        <Link
          to="/"
          className="inline-flex h-10 items-center rounded-lg border border-ink-200 px-4 text-sm font-medium transition hover:bg-ink-50 dark:border-ink-700 dark:hover:bg-ink-800"
        >
          Back to Taskly
        </Link>
      </div>
    </main>
  )
}
